import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TreeModule } from 'primeng/tree';
import { ContaContabilService } from '@/core/api/sicom-api/financeiro/arrecadacao/conta-contabil/conta-contabil.service';
import { ContaContabilResponse } from '@/core/api/sicom-api/financeiro/arrecadacao/conta-contabil/conta-contabil.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/**
 * Modal de seleção de conta contábil em formato de árvore (plano de contas).
 *
 * Uso:
 *   <app-seleciona-conta-contabil #seletor (contaSelecionada)="onConta($event)" />
 *   // no componente: this.seletor.abrir();
 */
/** Diálogo de seleção de conta contábil (árvore do plano de contas) com busca e cache. */
@Component({
    selector: 'app-seleciona-conta-contabil',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, ButtonModule, DialogModule, TreeModule],
    templateUrl: './seleciona-conta-contabil.html',
    styleUrl: './seleciona-conta-contabil.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelecionaContaContabil {
    private readonly contaContabilService = inject(ContaContabilService);
    private readonly destroyRef = inject(DestroyRef);
    private contasCache: ContaContabilResponse[] | null = null;

    readonly header = input('Plano de Contas');
    readonly contaSelecionada = output<ContaContabilResponse>();

    protected readonly visivel = signal(false);
    protected readonly buscando = signal(false);
    protected readonly contas = signal<ContaContabilResponse[]>([]);
    protected readonly contaTree = computed<TreeNode[]>(() => this.buildTree(this.contas()));

    abrir(): void {
        this.visivel.set(true);
        if (!this.contasCache) {
            this.carregar();
        }
    }

    protected fechar(): void {
        this.visivel.set(false);
    }

    protected onNodeSelect(event: { node: TreeNode }): void {
        const conta = event.node.data as ContaContabilResponse;
        if (!conta) return;
        this.contaSelecionada.emit(conta);
        this.visivel.set(false);
    }

    private carregar(): void {
        this.buscando.set(true);
        this.contaContabilService
            .listar()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.buscando.set(false))
            )
            .subscribe({
                next: (contas) => {
                    this.contasCache = contas;
                    this.contas.set(contas);
                },
                error: () => this.contas.set([])
            });
    }

    private buildTree(contas: ContaContabilResponse[]): TreeNode[] {
        const map = new Map<string, TreeNode>();
        const roots: TreeNode[] = [];

        for (const conta of contas) {
            map.set(conta.id, {
                key: conta.id,
                label: `${conta.id}  ${conta.descricao}`,
                data: conta,
                children: [],
                expanded: !conta.id_pai
            });
        }

        for (const conta of contas) {
            const node = map.get(conta.id)!;
            const temPaiValido = conta.id_pai != null && conta.id_pai !== conta.id && map.has(conta.id_pai);
            if (temPaiValido) {
                map.get(conta.id_pai!)!.children!.push(node);
            } else {
                roots.push(node);
            }
        }

        const visitados = new Set<string>();
        const markLeaves = (nodes: TreeNode[]): void => {
            for (const node of nodes) {
                if (visitados.has(node.key!)) continue;
                visitados.add(node.key!);
                if (!node.children || node.children.length === 0) {
                    delete node.children;
                    node.leaf = true;
                    node.icon = 'pi pi-tag';
                } else {
                    node.icon = 'pi pi-folder-open';
                    markLeaves(node.children);
                }
            }
        };
        markLeaves(roots);

        return roots;
    }
}
